import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Props {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const btnStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid var(--nx-border)',
  background: 'var(--nx-surface-1, #fff)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  display: 'flex',
  alignItems: 'center',
});

/** Prev/next controls plus a type-to-jump page number input (1-indexed for the user). */
function PaginationControl({ page, totalPages, onPageChange }: Props) {
  const [inputValue, setInputValue] = useState(String(page + 1));

  useEffect(() => {
    setInputValue(String(page + 1));
  }, [page]);

  const commit = () => {
    const n = parseInt(inputValue, 10);
    if (Number.isFinite(n) && n >= 1 && n <= totalPages) {
      if (n - 1 !== page) onPageChange(n - 1);
    } else {
      setInputValue(String(page + 1));
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button onClick={() => onPageChange(page - 1)} disabled={page === 0} style={btnStyle(page === 0)}>
        <ChevronLeft size={15} />
      </button>
      <span style={{ fontSize: 13, color: 'var(--nx-text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        Page
        <input
          type="number"
          value={inputValue}
          min={1}
          max={totalPages}
          onChange={e => setInputValue(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
          style={{
            width: 44,
            padding: '4px 6px',
            textAlign: 'center',
            background: 'var(--nx-surface-3)',
            border: '1px solid var(--nx-border)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--nx-text-primary)',
            outline: 'none',
          }}
        />
        of {totalPages}
      </span>
      <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages - 1} style={btnStyle(page >= totalPages - 1)}>
        <ChevronRight size={15} />
      </button>
    </div>
  );
}

export default PaginationControl;
